import experiment from 'experiment';
import { type AnySection, SectionType } from '@wix/add-panel-component';
import { styleManager } from '#packages/core';
import type { EditorAPI } from '#packages/editorAPI';
import { PANEL_TYPES } from '#packages/addPanelDataConsts';
import addPanelStyleManager from '../../addPanel/addPanelStyleManager';
import componentsDefaultData from '../../addPanel/componentsDefaultData';
import type { StyleForSection } from './types';

const DISPLAYED_STYLES_LIMIT = 24;

const COMP_TYPE_DEPRECATION_MAP = {
  'wysiwyg.viewer.components.menus.DropDownMenu': {
    newCompType: 'wixui.StylableHorizontalMenu',
    specName: 'se_openMenuDeprecationNotificationBanner',
  },
};
type CompType = keyof typeof COMP_TYPE_DEPRECATION_MAP;

const getCompTypeAfterDeprecation = (
  compType: CompType,
  panelLayoutType: String,
) => {
  if (
    !COMP_TYPE_DEPRECATION_MAP[compType] ||
    panelLayoutType === PANEL_TYPES.DESIGN_PANEL
  ) {
    return compType;
  }
  const { newCompType, specName } = COMP_TYPE_DEPRECATION_MAP[compType];
  return experiment.isOpen(specName) ? newCompType : compType;
};

export const getStylesForPersonalizedSection = ({
  panelLayoutType = PANEL_TYPES.ADD_PANEL,
  compTypes,
  editorAPITheme,
  editorAPIComponents,
}: AnyFixMe): StyleForSection[] => {
  const stylesForSection: AnyFixMe = [];

  compTypes.forEach(function (compType: string) {
    const compDef = componentsDefaultData.getComponentDefault(
      compType,
      panelLayoutType,
    );

    if (!compDef) {
      return;
    }

    const componentType =
      compDef.componentTypeSkinOverride || compDef.componentType || compType;

    const styles = styleManager.getUniqueCustomStyleList(
      editorAPITheme,
      editorAPIComponents,
      componentType,
    );

    if (!styles || styles.length === 0) {
      return;
    }

    styles.slice(0, DISPLAYED_STYLES_LIMIT).forEach(function (style) {
      stylesForSection.push({
        styleId: style.id,
        styleDef: style,
        compType,
      });
    });
  });

  return stylesForSection;
};

export const getStylesForThemedSection = ({
  panelLayoutType = PANEL_TYPES.ADD_PANEL,
  compTypes,
  editorAPITheme,
  editorAPIComponents,
}: AnyFixMe): StyleForSection[] => {
  const stylesForSection: AnyFixMe = [];

  compTypes.forEach(function (_compType: string) {
    const compType = getCompTypeAfterDeprecation(
      _compType as CompType,
      panelLayoutType as string,
    );

    const compDef = componentsDefaultData.getComponentDefault(
      compType,
      panelLayoutType,
    );
    if (!compDef) {
      return;
    }

    const componentType =
      compDef.componentTypeSkinOverride || compDef.componentType || compType;

    const styles = addPanelStyleManager.getComponentThemeStyles(
      componentType,
      editorAPITheme,
      editorAPIComponents,
    );

    if (!styles || styles.length === 0) {
      return;
    }

    styles.forEach(function (style) {
      stylesForSection.push({ styleId: style.id, styleDef: style, compType });
    });
  });

  return stylesForSection;
};

const getStylesForSection = ({
  section,
  editorAPITheme,
  editorAPIComponents,
}: AnyFixMe): StyleForSection[] => {
  const { props } = section;
  const { compTypes } = props;

  return section.type === SectionType.Theme
    ? getStylesForThemedSection({
        compTypes,
        editorAPITheme,
        editorAPIComponents,
      })
    : getStylesForPersonalizedSection({
        compTypes,
        editorAPITheme,
        editorAPIComponents,
      });
};

export const hasStylesForSection = (
  editorAPI: EditorAPI,
  section: AnySection,
) => {
  const stylesForSection = getStylesForSection({
    section,
    editorAPITheme: editorAPI.theme,
    editorAPIComponents: editorAPI.components,
  });
  return stylesForSection.length > 0;
};
